import { useMemo, useState } from 'react';
import { ErrorBanner, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';

/**
 * Data Room — the A–I due diligence folder index.
 *
 * Structure only. No documents live in this app: the index records what has
 * been asked for and what has arrived, which makes it a live checklist without
 * becoming a place confidential files pile up.
 */
const STATUSES = [
  { value: 'pending', label: 'Not requested' },
  { value: 'requested', label: 'Requested' },
  { value: 'received', label: 'Received' },
  { value: 'na', label: 'N/A' },
] as const;

interface Category {
  MaDataRoomCategoryId: string;
  Code: string;
  Name: string;
  SortOrder: number;
}
interface Item {
  MaDataRoomItemId: string;
  MaDataRoomCategoryId: string;
  Code: string;
  Name: string;
  Status: string;
  Notes: string;
  IsCustom: boolean;
  SortOrder: number;
}

export function MaDataRoom() {
  const data = useResource<{ categories: Category[]; items: Item[] }>('/ma/data-room');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const categories = useMemo(() => data.data?.categories ?? [], [data.data]);
  const items = useMemo(() => data.data?.items ?? [], [data.data]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(
      (i) =>
        (status === '' || i.Status === status) &&
        (q === '' || i.Name.toLowerCase().includes(q) || i.Code.toLowerCase().includes(q)),
    );
  }, [items, search, status]);

  const save = async (id: string, patch: Record<string, unknown>) => {
    setProblem(null);
    try {
      await api.put(`/ma/data-room/items/${id}`, patch);
      data.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save');
    }
  };

  if (data.loading) return <Loading label="Loading the data room" />;
  if (data.error) return <ErrorBanner message={data.error} />;

  const received = items.filter((i) => i.Status === 'received').length;

  return (
    <div className="stack">
      {problem && <ErrorBanner message={problem} />}

      <div className="card">
        <div className="card-body tight">
          <div className="row wrap">
            <input
              className="input"
              style={{ maxWidth: 300 }}
              placeholder="Search the index"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="select"
              style={{ maxWidth: 190 }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Any status</option>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <span className="grow" />
            <span className="xs muted">
              <span className="mono">{received}</span> of <span className="mono">{items.length}</span> received
            </span>
          </div>
        </div>
      </div>

      {categories.map((cat) => {
        const rows = visible.filter((i) => i.MaDataRoomCategoryId === cat.MaDataRoomCategoryId);
        const all = items.filter((i) => i.MaDataRoomCategoryId === cat.MaDataRoomCategoryId);
        const got = all.filter((i) => i.Status === 'received').length;
        if (rows.length === 0 && (search || status)) return null;

        return (
          <div className="card" key={cat.MaDataRoomCategoryId}>
            <div className="card-head">
              <div className="row-between">
                <h2>
                  <span className="mono subtle">{cat.Code}.</span> {cat.Name}
                </h2>
                <span className="xs muted">
                  <span className="mono">{got}</span>/<span className="mono">{all.length}</span> received
                </span>
              </div>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Ref</th>
                    <th>Item</th>
                    <th style={{ width: 170 }}>Status</th>
                    <th style={{ width: 260 }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={item.MaDataRoomItemId}>
                      <td className="mono xs">{item.Code}</td>
                      <td>{item.Name}</td>
                      <td>
                        <select
                          className="select"
                          value={item.Status}
                          onChange={(e) =>
                            void save(item.MaDataRoomItemId, { status: e.target.value })
                          }
                        >
                          {STATUSES.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {/* Saved on blur rather than per keystroke — a note is
                            written in one go, and a request per character
                            would be a request per character. */}
                        <input
                          className="input"
                          defaultValue={item.Notes}
                          placeholder="—"
                          onBlur={(e) => {
                            if (e.target.value !== item.Notes) {
                              void save(item.MaDataRoomItemId, { notes: e.target.value });
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
